import { IProjectInfo } from 'library/ts/IProjectInfo';
import { ProjectInfo } from 'library/ts/ProjectInfo';
import { Country } from 'library/ts/model/enums/geo/Country';
import { Domain } from 'library/ts/model/enums/web/Domain';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { removeDuplicates } from 'library/ts/utilities/modules/Array';
import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import * as StringModule from 'library/ts/utilities/modules/String';
import { hasSubstring } from 'library/ts/utilities/modules/String';
import * as Type from 'library/ts/utilities/modules/Type';

const logger = LogService.create( { prefixes: [ 'SmartTag:' ] } );

/**
 * @description Get referrer URL from the top
 * @return {String}
 */
export const getTopReferrer = (): string => {
	let url: string = ''; // init url placeholder var

	try {
		url = window.top.location.href;
	} catch ( error ) {
		// error( 'Utilities.getTopReferrer: 01: ERROR!: ' + error.message );
	}

	if ( !url ) {
		url = ''; // initialize url placeholder
		let tmpWindow: any = window; // get window reference

		let i = 0;

		try {
			for ( i = 15; ( i > 0 ) && ( tmpWindow.parent !== null ); i-- ) {
				if ( tmpWindow.location.length > 0 ) {
					url = tmpWindow.location;
				}
				if ( tmpWindow.document.referrer.length > 0 ) {
					if ( tmpWindow !== tmpWindow.top ) url = tmpWindow.document.referrer;
				}
				tmpWindow = tmpWindow.parent;
			}
		} catch ( error ) {
			// logger.log( 'getTopReferrer: 02: index: ' + i + ' ERROR!: ' + error.message );
		}
	}

	return url;
};

/**
 * @description URL parser
 * @param myURL {String} f passed use for parsing, otherwise use top referrer
 * @return {{protocol:String, host:String, hostname:String, hostnameShort:String, port:String, pathname:String, path:String, hash:String, search:String}}
 */
export const parser = ( myURL: string = getTopReferrer() ): {
	protocol: string,
	host: string,
	hostname: string,
	hostnameShort: string,
	port: string,
	pathname: string,
	path: string,
	hash: string,
	search: string
} => {
	const urlString = ( myURL === undefined ? getTopReferrer() : myURL );

	const out = {
		hash: undefined,
		host: undefined,
		hostname: undefined,
		hostnameShort: undefined,
		path: undefined,
		pathname: undefined,
		port: undefined,
		protocol: undefined,
		search: undefined
	};

	// sanity check
	if ( !Type.isFunction( document?.createElement ) ) {
		return out;
	}

	const url = document.createElement( 'a' );
	url.href = urlString;

	out.protocol = url.protocol; // => "http:"
	out.protocol = out.protocol.replace( ':', '' ); // clean up
	out.host = url.host; // => "example.com:3000"
	out.host = out.host.split( ':' )[ 0 ]; // remove port
	out.hostname = url.hostname; // => "example.com"

	out.hostnameShort = out.hostname; // assign hostnameShort as full hostname
	if ( out.hostnameShort.indexOf( 'www.' ) === 0 ) out.hostnameShort.replace( 'www.', '' ); // strip www from  www.domain.com

	out.port = url.port; // => "3000"
	out.pathname = url.pathname; // => "/pathname/"
	if ( out.pathname.substr( 0, 1 ) !== '/' ) out.pathname = '/' + out.pathname; // fix for IE
	out.path = out.pathname.substring( 0, out.pathname.lastIndexOf( '/' ) ) + '/'; // path without file name
	out.hash = url.hash; // => "#hash"
	out.search = url.search; // => "?search=test"

	return out;
};

/**
 * @description Removes URL param
 * @see {@url http://stackoverflow.com/questions/1634748/how-can-i-delete-a-query-string-parameter-in-javascript}
 * @param param {String}
 * @param url {String}
 * @return {String}
 * @example
 * ```typescript
 * url = location.href; // -> http://example.com/?tags[]=single&tags[]=promo&sold=1
 * url = removeParam("sold", url) // -> http://example.com/?tags[]=single&tags[]=promo
 * url = removeParam("tags", url) // -> http://example.com/
 * ```
 */
export const removeParam = ( param: string, url: string ): string => {
	const urlArray = decodeURI( url ).split( '?' );
	let path = urlArray.length === 1 ? '' : urlArray[ 1 ];
	path = path.replace( new RegExp( '&?' + param + '\\[\\d*\\]=[\\w]+', 'g' ), '' );
	path = path.replace( new RegExp( '&?' + param + '=[\\w]+', 'g' ), '' );
	path = path.replace( /^&/, '' );
	return urlArray[ 0 ]
		+
		(
			path.length
				? '?' + path
				: ''
		);
};

/**
 * @description Adds URL param
 * @see {@url http://stackoverflow.com/questions/1634748/how-can-i-delete-a-query-string-parameter-in-javascript}
 * @param param {String}
 * @param val {String|Object}
 * @param url {String}
 * @param useDecodeURI {Boolean} flag for turning on use of decodeURI on some params that contain URL encoded characters
 * @return {String}
 * @example
 * ```typescript
 * url = location.href; // -> http://example.com/?tags[]=single&tags[]=promo&sold=1
 * url = addParam("tags", ["single", "promo"], url) // -> http://example.com/?tags[]=single&tags[]=promo
 * url = addParam("sold", 1, url) // -> http://example.com/?tags[]=single&tags[]=promo&sold=1
 * ```
 */
export const addParam = ( param: string, val: any, url: string, useDecodeURI: boolean = false ): string => {
	if ( typeof val === TrueType.OBJECT ) {
		// recursively add in array structure
		if ( val.length ) {
			return addParam(
				param + '[]',
				val.splice( -1, 1 )[ 0 ],
				addParam( param, val, url )
			);
		} else {
			return url;
		}
	} else {
		const urlArray = ( useDecodeURI ? decodeURI( url ).split( '?' ) : url.split( '?' ) );
		let path = urlArray.length === 1 ? '' : urlArray[ 1 ];
		path += path.length
			? '&'
			: '';
		path += ( useDecodeURI ? decodeURI( param + '=' + val ) : ( param + '=' + val ) );
		return urlArray[ 0 ] + '?' + path;
	}
};

/**
 * @description Replaces URL param
 * @see {@url http://stackoverflow.com/questions/1634748/how-can-i-delete-a-query-string-parameter-in-javascript}
 * @param param {String}
 * @param val {String|Object}
 * @param url {String}
 * @param useDecodeURI {Boolean} flag for turning on use of decodeURI on some params that contain URL encoded characters
 * @return {String}
 * @example
 * ```typescript
 * url = location.href; // -> http://example.com/?promo=5
 * url = replaceParam("promo", '6', url) // -> http://example.com/?promo=6
 * ```
 */
export const replaceParam = ( param: string, val: any, url: string, useDecodeURI: boolean = false ): string => {
	let curURL = ( useDecodeURI ? decodeURI( url ) : url );
	if ( getQueryField( param, curURL ) ) {
		const pattern = new RegExp( '\\b(' + name + '=).*?(&|#|$)' );
		if ( url.search( pattern ) >= 0 ) {
			curURL = url.replace( pattern, '$1' + val + '$2' );
		}
	} else {
		curURL = addParam( param, val, url, useDecodeURI );
	}
	return curURL;
};

// add  replace param

/**
 * @description Checks if URL has a protocol and returns Boolean
 * @param url {String}
 * @param protocol {String}
 * @return {Boolean}
 */
export const hasProtocol = ( url: string = '', protocol: string = 'http' ): boolean => {
	return url.split( '/' )[ 0 ].indexOf( protocol.toLowerCase() ) !== -1;
};

/**
 * @description Checks if string is a valid URL
 * @param string {String}
 * @return {Boolean}
 */
export const valid = ( string: string ): boolean => {
	if ( Type.isString( string ) === false ) return false; // sanity check

	if ( string.indexOf( 'http://localhost' ) === 0 || string.indexOf( 'https://localhost' ) === 0 ) return true;

	try {
		// PLBCK-5070: Use URL constructor instead of URL.canParse() for Chrome 62 compatibility
		// URL.canParse() requires Chrome 120+, but Samsung Tizen uses Chrome 62
		// The URL constructor has been available since Chrome 32
		new URL( string );
		return true;
	} catch ( error ) {
		return false;
	}
};

/**
 * @description Checks if string is a valid URL using http or https protocol
 * @param string {String}
 * @return {Boolean}
 */
export const isHttpUrl = ( string: string ): boolean => {
	if ( valid( string ) === false ) {
		return false;
	}

	try {
		const url = new URL( string );
		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch ( error ) {
		return false;
	}
};

/**
 * @description Filters a value down to valid http/https URLs
 * @param urls {unknown}
 * @return {string[]}
 */
export const filterHttpUrls = ( urls: unknown ): string[] => {
	if ( !Array.isArray( urls ) ) {
		return [];
	}

	return urls.filter(
		( url ) => isHttpUrl( url ) === true
	);
};

/**
 * @description Returns file extension for filename
 * @param {string} filename
 * @param {boolean} clean - file extension without query string
 * @return {string | undefined}
 */
export const getFileExtension = ( filename: string, clean: boolean = true ): string | undefined => {
	let out: string | undefined = ( /[.]/.exec( filename ) ) ? /[^.]+$/.exec( filename )[ 0 ] : undefined;
	if ( Type.isString( out ) && clean === true ) out = out.split( '?' )[ 0 ];
	return out;
};

/**
 * @description Returns URL query string variable value
 * @param field {String} URL query variable
 * @param url {String} URL
 * @return {String|undefined}
 */
export const getQueryField = ( field: string, url?: string ): string | undefined => {
	const href = url ? url : window.location.href;
	const reg = new RegExp( '[?&]' + field + '=([^&#]*)', 'i' );
	const string = reg.exec( href );
	return string ? ( StringModule.trim( string[ 1 ] ) !== '' ? sanitize( StringModule.trim( string[ 1 ] ) ) : null ) : undefined;
};

/**
 * @description Parses URL query string to Object
 * @param url {String}
 * @return {Record<string, any>}
 */
export const queryStringToObject = ( url: string ): Record<string, any> => {
	// if we have url with params separate url base from search / params
	if ( url.indexOf( '?' ) > -1 ) url = url.split( '?' )[ 1 ];

	const urlParams: Record<string, any> = {};

	const replacer = ( $0, $1, $2, $3 ): any => {
		urlParams[ $1 ] = $3;
		return urlParams;
	};

	url.replace(
		new RegExp( '([^?=&]+)(=([^&]*))?', 'g' ),
		replacer
	);

	return urlParams;
};

/**
 * @description Converts query string to object
 * @param {Record<string, any>} object
 * @return {string}
 */
export const objectToQueryString = ( object: Record<string, any> = {} ): string => {
	return Object.keys( object )
		.map(
			( key: string ) => {
				return `${key}=${object[ key ].toString()}`;
			}
		)
		.join( '&' );
};

/**
 * @description Converts URL to cache busted one by adding time param
 * @param url {String}
 * @param cacheBust {Boolean} if true - add &time=XXXXXXXX param
 * @return {String}
 */
export const cacheBust = ( url: string, cacheBust: boolean ): string => {
	if ( cacheBust === true ) {
		url.indexOf( '?' ) > -1 ? url += '&time=' + new Date().getTime() : url += '?time=' + new Date().getTime();
	}
	return url;
};

/**
 * @description Converts URL to HTTPS
 * @param url {String}
 * @param all {Boolean} if true - converts all instances of http in url i.e. http in URL params
 * @return {String}
 */
export const toHTTPS = ( url: string, all: boolean = true ): string => {
	if ( url === undefined ) return url;
	const query = 'http://';
	const reg = new RegExp( query, ( all === true ? 'gi' : '' ) );
	url = url.replace( reg, 'https://' );
	return url;
};

/**
 * @description Convert array of URLs to SSL
 * @param urls {Array.<String>} array of URLs
 * @return {Array.<String>}
 */
export const urlsToHTTPS = ( urls: any ): string[] => {
	if ( Type.isArray( urls ) === false ) urls = [ urls ]; // sanity check
	for ( let i = 0; i < urls.length; i++ ) {
		urls[ i ] = toHTTPS( urls[ i ], true );
	}
	return urls;
};

/**
 * @description Returns true if application is embedded on Paramount domains
 * @param {string[]} domains
 * @return {boolean}
 */
export const isPARA = ( domains: string[] = [] ): boolean => {
	return hasSubstring(
		parser().hostnameShort.toLowerCase(),
		// concatenate domains + remove duplicates
		removeDuplicates(
			[
				Domain.PPLUS,
				Domain.CBSCOM,
				Domain.PTV,
				'cbsallaccess.ca',
				'allaccess.com.au',
				'localhost',
				'googleapis.com',
				'ngrok.io'
			].concat( domains )
		)
	);
};

/**
 * @description What domain are we embedded at?
 * @param {string} host
 * @returns {Domain | undefined}
 */
export const myDomain = ( host: string = parser()?.host ): Domain | undefined => {
	for ( const key in Domain ) {
		const value: Domain = Domain[ key ];
		if ( host.includes( value ) ) {
			return value as Domain;
		}
	}
	return undefined;
};

/**
 * @description Checks if origin string/domain is allowed
 * @param {string} origin
 * @param {string[]} allowedOrigins
 * @returns {boolean}
 */
export const originAllowed = (
	origin: string = '',
	allowedOrigins: string[] = [
		'localhost',
		'paramountplus.com',
		'cbs.com',
		'tags.tiqcdn.com'
	]
): boolean => {
	const allowedRegexStr = allowedOrigins.join( '|' ).replace( /\//g, '\/' );
	const allowedRegEx = new RegExp( allowedRegexStr );
	return origin.match( allowedRegEx ) !== null;
};

/**
 * @description Returns country string detected from top level URL
 * @param url {String}
 * @return {String}
 */
export const countryFromURL = ( url: string = getTopReferrer() ) => {
	const topURL = url; // get top level URL

	let matchFound = false;
	let outputCountry = undefined;

	/**
	 * @description DB of P+ domains for international environment tests... domain.indexOf( 'cbs.com' ) !== -1
	 * @type {{P_PLUS_AU: {country: string, test: string[]}, LOCALHOST: {country: string, test: string[]}, P_PLUS: {country: string, test: string[]}, P_PLUS_CA: {country: string, test: string[]}}}
	 */
	const TEST = {
		LOCALHOST: {
			test: [
				'localhost'
			],
			country: Country.LOCALHOST
		},
		P_PLUS: {
			test: [
				Domain.PPLUS,
				Domain.CBSCOM,
				'googleapis.com',
				'ngrok.io'
			],
			country: Country.US
		},
		P_PLUS_CA: {
			test: [
				'cbsallaccess.ca',
				'tv.cbs.com/ca'
			],
			country: Country.CA
		},
		P_PLUS_AU: {
			test: [
				'allaccess.com.au',
				'tv.cbs.com/au'
			],
			country: Country.AU
		}
	};

	// loop over test conditions
	Object.keys( TEST ).forEach(
		( key ) => {
			const value = TEST[ key ];
			if ( matchFound === false ) {
				if ( hasSubstring( topURL, value.test ) ) {
					matchFound = true; // stop searching
					outputCountry = value.country; // set output country
				}
			}
		}
	);

	if ( outputCountry === undefined ) outputCountry = Country.US; // default country US

	return outputCountry;
};

/**
 * @description Checks if current application is loaded by iframe
 * @see {@link https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t}
 * @return {Boolean}
 */
export const isIFrame = (): boolean => {
	try {
		return window.self !== window.top;
	} catch ( error ) {
		return true;
	}
};

/**
 * @description Reload page
 * @param forceGet {Boolean} true - forces page reload from server
 */
export const reloadPage = ( forceGet: boolean = true ): void => {
	// process iframe embed case
	if ( window.self !== window.top ) { // iframe

		// @ts-ignore
		window.top.location.reload( forceGet );
	} else { // straight application embed

		// @ts-ignore
		window.location.reload( forceGet );
	}
};

/**
 * @description Checker for URL SSL protocol
 * @param url {String}
 * @return {Boolean}
 */
export const isSSL = ( url: string = getTopReferrer() ): boolean => {
	return ( url.split( '://' )[ 0 ].toLowerCase() === 'https' );
};

/**
 * @description Appends URL cache token to URL
 * @param url {String}
 * @param cacheBust {Boolean} Cache bust requested via &cacheBust=1 in browser URL?
 * @return {String|*}
 */
export const addCacheToken = ( url: string | any, cacheBust: boolean = false ): string => {
	const token = '70';
	if ( getQueryField( 'cb', url ) === undefined ) url = addParam( 'cb', token, url );
	if ( cacheBust === true && getQueryField( 'ts', url ) === undefined ) url = addParam( 'ts', new Date().getTime(), url );
	return url;
};

/**
 * @description Blind image loader (for i.e. tracking pixels)
 * @param {string} imageURL
 */
export const blindImageLoader = ( imageURL: string ): void => {
	if ( valid( imageURL ) === false ) return; // sanity check
	const img: HTMLImageElement = document.createElement( 'img' );
	img.height = 1;
	img.width = 1;
	img.style.border = 'none';
	img.src = imageURL;
};

/**
 * @description Returns response headers for Request
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch|Fetch}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Request/Request|Request}
 * @param {Request} request
 * @return {Promise<any>}
 */
export const getHeaders = ( request: Request ): Promise<any> => {
	return new Promise(
		( resolve, reject ) => {
			// sanity check
			if ( Type.isUndefined( request ) || Type.isUndefined( request?.url ) ) {
				logger.warn( 'Utilities.URL.getHeaders: ERROR:', 'request:', request );
				reject( { error: 'Empty request URL!' } );
			} else {
				logger.log( 'Utilities.URL.getHeaders:', 'request:', request );
			}

			// create output placeholder object
			const out: Record<string, any> = {};

			fetch(
				request
			)
				.then(
					( response ) => {
						for ( const header of response.headers ) {
							// store header key: value in response object
							out[ header[ 0 ] ] = header[ 1 ];
						}
						resolve( out );
					}
				)
				.catch(
					( error ) => {
						reject( error );
					}
				)
				.finally(
					() => {
						resolve( out );
					}
				);
		}
	);
};

/**
 * @description Returns sanitized HTML string
 * @param {string} input
 * @return {string}
 */
export const sanitize = ( input: string ): string => {
	const element = document.createElement( 'div' );
	element.innerText = input;
	return element.innerHTML;
};

/**
 * @description Checks for URL with valid file extension
 * @param {string} url
 * @param {string[]} extensions
 * @returns {boolean}
 */
export const isFileExtURL = ( url: string, extensions: string[] = [ 'apng', 'avif', 'gif', 'jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp', 'png', 'svg', 'webp' ] ): boolean => {
	if ( valid( url ) ) {
		return hasSubstring( getFileExtension( parser( url ).pathname.toLowerCase() ), extensions );
	}
	return false;
};

/**
 * @description Rewrites an absolute import URL to runtimeVO.url.root + the matched
 * directory/filename pattern from projectInfo.
 *
 * Matches both the default `<basename>.css` / `.js` filename and brand-suffix
 * variants (`<basename>.<brand>.css`) stored as nested objects under projectInfo
 * (`app_output_file.<app>.<ext>.<type>.<brand>.value`). Returns the URL unchanged
 * if no pattern matches.
 *
 * @param importURL - Absolute URL to rewrite. Falls back to an empty string.
 * @returns The URL rewritten to live under runtimeVO.url.root, or the original
 * URL if nothing matched.
 */
export const convertToRelative = ( importURL: string = '' ): string => {
	const runtimeVO = window?.SmartTag?.runtimeVO;
	const projectInfo: IProjectInfo = ProjectInfo;

	// Tries to match importURL against `<output_dir>/<filename>` (with the css→/css/
	// insertion the original pattern relied on) and, if found, rewrites importURL
	// to runtimeVO.url.root + that pattern. Mutates importURL in the enclosing scope.
	const tryRewriteForFilename = ( output_dir: string, filename: string ): void => {
		let dir_file_pattern: string = output_dir + '/' + filename;
		// adjust dir file pattern for css accommodating app-dir/css/file-name.css pattern
		if ( dir_file_pattern.indexOf( '.css' ) > -1 ) {
			dir_file_pattern = dir_file_pattern.replace( '/', '/css/' );
		}
		// check if import URL has dir_file_pattern
		if ( importURL.indexOf( dir_file_pattern ) > -1 ) {
			// split import URL so if we have any URL params we can attach them to converted URL
			const importURLSplit: string[] = importURL.split( dir_file_pattern );
			// converted URL => root + dir file pattern
			let convertedURL: string = runtimeVO.url.root + dir_file_pattern;
			// if we had import URL params => append them
			if ( importURLSplit.length > 1 ) convertedURL += importURLSplit[ 1 ];
			// assign converted URL to our importURL
			importURL = convertedURL;
		}
	};

	// loop over output dirs node for Smart Tag applications
	Object.keys( projectInfo.app_output_dir ).forEach(
		( app_name ) => {
			// store output dir for later use
			const output_dir: string = projectInfo.app_output_dir[ app_name ].value;
			// if app_name is defined
			if ( Type.isDefined( projectInfo.app_output_file[ app_name ] ) ) {
				// loop over app output files node
				Object.keys( projectInfo.app_output_file[ app_name ] ).forEach(
					( file_ext ) => {
						// loop over output file extensions
						Object.keys( projectInfo.app_output_file[ app_name ][ file_ext ] ).forEach(
							( type ) => {
								// loop over type: min or other
								Object.keys( projectInfo.app_output_file[ app_name ][ file_ext ][ type ] ).forEach(
									( key ) => {
										const node = projectInfo.app_output_file[ app_name ][ file_ext ][ type ][ key ];
										// Default filename: `{ value: '<basename>.css' }` at this level
										if ( key === 'value' && Type.isString( node ) ) {
											tryRewriteForFilename( output_dir, node );
										}
										// Brand variants: `{ pplus: { value: '<basename>.pplus.css' } }`,
										// `{ plutotv: { value: '...' } }`, `{ cbs: { value: '...' } }` etc.
										// Iterate any sibling-of-value object that exposes its own `value` string.
										else if ( Type.isObject( node ) && Type.isString( node.value ) ) {
											tryRewriteForFilename( output_dir, node.value );
										}
									}
								);
							}
						);
					}
				);
			}
		}
	);

	// INFO: process VTG player urls to be relative
	let vtgPlayerDirPattern: string = projectInfo.dependencies.bundle.tag.dir.value + '/' +
		projectInfo.dependencies.bundle.tag.player.dir.output.value + '/' +
		projectInfo.dependencies.bundle.tag.player.version.player_version_token;
	if ( vtgPlayerDirPattern.charAt( vtgPlayerDirPattern.length - 1 ) !== '/' ) vtgPlayerDirPattern = ( vtgPlayerDirPattern + '/' );

	if ( importURL.indexOf( vtgPlayerDirPattern ) > -1 ) {
		const splitURL = importURL.split( vtgPlayerDirPattern );
		const originURL = getOrigin( runtimeVO.url.root );
		importURL = ( originURL + vtgPlayerDirPattern + splitURL[ splitURL.length - 1 ] );
	}

	// return import URL
	return importURL;
};

/**
 * @description Extracts the origin (protocol + host) from a full URL and appends a trailing slash.
 * @param {string} url The full URL from which to extract the origin.
 * @returns {string} The origin portion of the URL with a trailing slash.
 */
export const getOrigin = ( url: string ): string => {
	const a = document.createElement( 'a' );
	a.href = url;

	const port = a.port ? `:${a.port}` : '';
	return `${a.protocol}//${a.hostname}${port}/`;
};

/**
 * @description Percent-encodes literal dots in a URL's query string as `%2E`.
 *
 * Shaka (4.16+) guesses the manifest type from the URL when no explicit mime type is
 * passed to `player.load()` — avia's ShakaAdapter never passes one. Its extension
 * sniffer scans the full URL backwards and returns everything after the last dot, so
 * a dot inside any query value (GPP consent strings, FreeWheel `imafw_host=www.x.com`,
 * dotted tracking IDs) yields a garbage extension instead of `mpd`/`m3u8`. Shaka then
 * falls back to a HEAD probe and fails with UNABLE_TO_GUESS_MANIFEST_TYPE (Shaka 4000
 * → avia 2103). `.` is an unreserved character (RFC 3986 §2.3), so `%2E` decodes to
 * the identical value server-side; with no literal dot left in the query, Shaka's scan
 * reaches the `?`, truncates, and resolves the real extension correctly.
 *
 * Idempotent — `%2E` contains no dots, so re-applying is a no-op. The path segment is
 * never modified. URLs without a query string are returned unchanged.
 *
 * @param {string} url - Fully-built media URL
 * @returns {string} URL with query-string dots percent-encoded; path untouched
 */
export const escapeQueryDots = ( url: string ): string => {
	if ( !Type.isString( url ) ) {
		return url;
	}
	const queryIndex = url.indexOf( '?' );
	if ( queryIndex === -1 ) {
		return url;
	}
	return url.slice( 0, queryIndex + 1 ) + url.slice( queryIndex + 1 ).replace( /\./g, '%2E' );
};